import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { COMPANY_INFO, mockSubmitForm } from '../data/mock';
import { Phone, Mail, MessageCircle, MapPin, Loader2, ArrowRight } from 'lucide-react';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await mockSubmitForm('contact', formData);
    setIsSubmitting(false);
    navigate('/thank-you/contact');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#F8FAFC] to-white py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-[#111111]">
              Contact Us
            </h1>
            <p className="mt-6 text-xl text-[#475569] leading-relaxed">
              Have questions? We're here to help. Get in touch and we'll respond as quickly as we can.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-[#111111] mb-8">Get in touch</h2>
              
              <div className="space-y-6">
                <Card className="border-[#E2E8F0]">
                  <CardContent className="p-6">
                    <a 
                      href={`tel:${COMPANY_INFO.phone}`}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#F0FDFA] flex items-center justify-center group-hover:bg-[#0D9488] transition-colors">
                        <Phone className="w-5 h-5 text-[#0D9488] group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm text-[#64748B]">Phone</p>
                        <p className="font-semibold text-[#111111]">{COMPANY_INFO.phone}</p>
                      </div>
                    </a>
                  </CardContent>
                </Card>

                <Card className="border-[#E2E8F0]">
                  <CardContent className="p-6">
                    <a 
                      href={`mailto:${COMPANY_INFO.email}`}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#F0FDFA] flex items-center justify-center group-hover:bg-[#0D9488] transition-colors">
                        <Mail className="w-5 h-5 text-[#0D9488] group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm text-[#64748B]">Email</p>
                        <p className="font-semibold text-[#111111]">{COMPANY_INFO.email}</p>
                      </div>
                    </a>
                  </CardContent>
                </Card>

                <Card className="border-[#E2E8F0]">
                  <CardContent className="p-6">
                    <a 
                      href={`https://wa.me/${COMPANY_INFO.whatsapp.replace(/\+/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] flex items-center justify-center group-hover:bg-[#25D366] transition-colors">
                        <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm text-[#64748B]">WhatsApp</p>
                        <p className="font-semibold text-[#111111]">Message us</p>
                      </div>
                    </a>
                  </CardContent>
                </Card>
              </div>

              {/* Service Area Reminder */}
              <div className="mt-8 p-6 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#111111]">Service Area</p>
                    <p className="text-sm text-[#64748B] mt-1">
                      Currently servicing Metro Melbourne LGAs only.
                    </p>
                    <Link 
                      to="/service-area"
                      className="inline-flex items-center gap-1 text-sm text-[#0D9488] hover:underline mt-2"
                    >
                      Check your postcode <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-[#111111] mb-8">Send us a message</h2>
              <Card className="border-[#E2E8F0]">
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">
                        Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Your name"
                        className="border-[#E2E8F0] focus:border-[#0D9488]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="you@example.com"
                        className="border-[#E2E8F0] focus:border-[#0D9488]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">
                        Phone
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="04XX XXX XXX"
                        className="border-[#E2E8F0] focus:border-[#0D9488]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">
                        Message *
                      </label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="How can we help?"
                        className="border-[#E2E8F0] focus:border-[#0D9488] min-h-[150px]"
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#111111] mb-8">Looking for something specific?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book/waste">
              <Button variant="outline" className="border-[#E2E8F0] text-[#111111] hover:border-[#0D9488]">
                Book Collection
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="border-[#E2E8F0] text-[#111111] hover:border-[#0D9488]">
                Request Pricing
              </Button>
            </Link>
            <Link to="/faqs">
              <Button variant="outline" className="border-[#E2E8F0] text-[#111111] hover:border-[#0D9488]">
                View FAQs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
